import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import DarkIconButton from '@/components/ui/Buttons/SecondaryIconButton'
import { useAuth } from '@/context/AuthContext'
import {
  ChoosenInfo,
  ContactsV2,
  DanPersonInfo,
  InstanceNewNumberV2
} from '@/services/mobi-api/numberv2/types'
import { resolveImageUrl } from '@/utils/constants'
import useIntl from '@/utils/intl'
import { Close, Done } from '@mui/icons-material'
import {
  Box,
  Divider,
  MobileStepper,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material'
import classNames from 'classnames'
import Image from 'next/image'
import { useSearchParams } from 'next/navigation'
import React, { FunctionComponent, useEffect } from 'react'
import Step1 from './step1'
import Step2 from './step2'
import Step3 from './step3'
import Step4 from './step4'
import OrderSummary from './orderSummary'

export interface Obj {
  instanceId?: string
  email?: string
  choosenPackage?: string
  choosenInfo?: ChoosenInfo
  choosenNumber?: string
  choosenSim?: string
  contactInfo?: ContactsV2[]
  rateplan?: string
  kycRedirectUrl?: string
  danUrl?: string
  selectedChildRegno?: string
  selectedChildInfo?: DanPersonInfo
  instance?: InstanceNewNumberV2
}

interface Props {
  onClose: () => void
}

const MffNumber: FunctionComponent<Props> = ({ onClose }) => {
  const { translate } = useIntl()
  const searchParams = useSearchParams()
  const { isWebView } = useAuth()
  const [currentStep, setCurrentStep] = React.useState(0)
  const [objs, setObj] = React.useState<Obj | undefined>()
  const [isIphoneClient, setIsIphoneClient] = React.useState<boolean>(false)

  const action = searchParams.get('step')

  useEffect(() => {
    if (typeof navigator !== 'undefined') {
      const userAgent = navigator.userAgent ?? (window as any).opera
      setIsIphoneClient(userAgent.includes('iPhone'))
    }
  }, [])

  useEffect(() => {
    if (action !== null && action !== '') {
      setCurrentStep(parseInt(action))
    }
  }, [action])

  const isStarsPackageSelected =
    (objs?.choosenInfo?.choosenPackage?.rateplan === 'PRSTA') ||
    (objs?.choosenPackage?.toLowerCase().includes('stars') ?? false)
  const hasOrderSummaryStep =
    objs?.choosenPackage?.toLowerCase()?.includes('mymobi') ?? false

  const steps = isStarsPackageSelected
    ? [
        {
          label: translate('number.step1Title'),
          description: translate('number.step1Desc')
        },
        {
          label: translate('number.step3Title'),
          description: ''
        },
        {
          label: translate('payment'),
          description: translate('vat.options')
        }
      ]
    : [
        {
          label: translate('number.step1Title'),
          description: translate('number.step1Desc')
        },
        ...(hasOrderSummaryStep
          ? [{
              label: translate('order.summary'),
              description: translate('order.status')
            }]
          : []),
        {
          label: translate('number.step2Title'),
          description: translate('number.step2Desc')
        },
        {
          label: translate('number.step3Title'),
          description: ''
        },
        {
          label: translate('payment'),
          description: translate('vat.options')
        }
      ]

  const handleNext = (obj: Obj): void => {
    const mergedObj = {
      ...objs,
      ...obj
    }
    setObj(mergedObj)
    setCurrentStep(currentStep + 1)
  }

  const exitWebview = (): void => {
    let webView
    if ((window as any).mobiBridge !== undefined) {
      webView = (window as any).mobiBridge
    }
    if ((window as any).ReactNativeWebview !== undefined) {
      webView = (window as any).ReactNativeWebview
    }
    if (webView !== undefined) {
      const data = { key: 'exit' }
      webView.postMessage(JSON.stringify(data))
    }
  }

  const stepIcon = (index: number): JSX.Element => {
    return (
      <>
        {index === currentStep && (
          <div className='bg-secondary p-1 rounded-lg md:h-6 md:w-6 w-5 h-5 flex justify-center items-center'>
            <span className='text-textInverse font-extrabold text-xs md:text-sm'>
              {index + 1}
            </span>
          </div>
        )}
        {index < (currentStep ?? 0) && (
          <div className='bg-[#F0FDF4] border border-[#16A34A] p-2 rounded-lg md:h-6 md:w-6 w-5 h-5 flex justify-center items-center'>
            <Done className='text-[#16A34A] text-xs md:text-sm font-bold' />
          </div>
        )}
        {index > (currentStep ?? 0) && (
          <div className='bg-[#231F1F1A] p-1 rounded-lg md:h-6 md:w-6 w-5 h-5 flex justify-center items-center'>
            <span className='text-[#B0B0B0] font-extrabold text-xs md:text-sm'>
              {index + 1}
            </span>
          </div>
        )}
      </>
    )
  }

  return (
    <div className='flex md:flex-row flex-col h-full md:h-screen overflow-hidden'>
      <div className='bg-surfaceInverse flex flex-col xl:w-1/4 min-h-0 overflow-y-auto'>
        <div className='lg:p-12 md:p-8 p-4'>
          <div className='md:flex flex-col items-start hidden'>
            <Image
              draggable={false}
              width={50}
              height={50}
              src={resolveImageUrl('/uploads/mobicom-logo-hq.png')}
              alt='logo'
              className='w-16'
            />
            <div className='mt-2'>
              <h1 className='lg:text-2xl md:text-xl font-bold text-textHigh'>
                {translate('quick.newNumber')}
              </h1>
              <span className='text-textMid text-xs 2xl:text-sm font-medium'>
                {translate('simple')}
              </span>
            </div>
          </div>
          <div className='md:hidden flex justify-between items-center'>
            <div className='flex items-center'>
              <h1 className='font-bold text-sm text-textHigh border-r pr-1 '>
                {translate('quick.newNumber')}
              </h1>
              <MobileStepper
                className='bg-surfaceInverse'
                color='error'
                variant='dots'
                sx={{
                  '.MuiMobileStepper-dotActive': {
                    backgroundColor: 'red'
                  }
                }}
                steps={steps.length}
                position='static'
                activeStep={currentStep}
                backButton={<></>}
                nextButton={<></>}
              />
              <div
                className={classNames(
                  '',
                  currentStep === 0 ? 'invisible' : 'visible'
                )}
              >
                <DarkButton
                  type='soft'
                  fullwidth
                  size='sm'
                  onClick={() => setCurrentStep(currentStep - 1)}
                >
                  {' '}
                  {translate('back')}
                </DarkButton>
              </div>
            </div>
            {!isWebView && isIphoneClient
              ? (
                <></>
                )
              : (
                <DarkIconButton
                  type='soft'
                  size='sm'
                  icon={<Close />}
                  onClick={isWebView ? exitWebview : onClose}
                />
                )}
          </div>
        </div>
        <Divider className='hidden md:flex bg-secondaryHighStroke' />
        <div className='lg:p-14 md:p-8'>
          <div className='hidden md:flex'>
            <Stepper orientation='vertical' activeStep={currentStep}>
              {steps.map((step, index) => (
                <Step key={index}>
                  <StepLabel StepIconComponent={() => stepIcon(index)}>
                    <Typography className='text-sm font-bold text-textHigh'>
                      {step.label}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography className='text-xs text-textMid font-medium'>
                      {step.description}
                    </Typography>
                    {index > 0
                      ? (
                        <Box sx={{ mt: 2 }}>
                          <DarkButton
                            type='soft'
                            size='sm'
                            onClick={() => setCurrentStep(index - 1)}
                          >
                            {translate('back')}
                          </DarkButton>
                        </Box>
                        )
                      : (
                        <></>
                        )}
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </div>
        </div>
      </div>
      <Divider
        orientation='vertical'
        className='bg-secondaryHighStroke'
        flexItem
      />
      <div className='flex-1 min-h-0 bg-surfaceLow overflow-y-auto'>
        <div className='flex justify-end w-full p-2 md:p-3'>
          <div className='hidden md:flex'>
            <DarkIconButton
              type='soft'
              size='sm'
              icon={<Close />}
              onClick={onClose}
            />
          </div>
        </div>
        <div className='flex justify-center items-center md:-mt-3'>
          {isStarsPackageSelected
            ? (
                currentStep === 0
                  ? (
                    <Step1 onClick={handleNext} />
                    )
                  : currentStep === 1
                    ? (
                    <Step3
                      onClick={handleNext}
                      instanceId={objs?.instanceId}
                      instance={objs?.instance}
                    />
                    )
                  : (
                    <Step4
                      instanceId={objs?.instanceId}
                      initialEmail={objs?.email}
                    />
                    )
              )
            : (
                currentStep === 0
                  ? (
                    <Step1 onClick={handleNext} />
                    )
                  : currentStep === 1 && hasOrderSummaryStep
                    ? (
                      <OrderSummary
                        objs={objs ?? {}}
                        onConfirm={() => handleNext({})}
                        onBack={() => setCurrentStep(0)}
                        instanceId={objs?.instanceId}
                      />
                      )
                    : currentStep === 1
                      ? (
                        <Step2
                          instanceId={objs?.instanceId}
                          onClick={(instance) => handleNext({ instance })}
                        />
                        )
                      : currentStep === 2 && hasOrderSummaryStep
                        ? (
                          <Step2
                            instanceId={objs?.instanceId}
                            onClick={() => handleNext({})}
                          />
                          )
                        : currentStep === (hasOrderSummaryStep ? 3 : 2)
                          ? (
                            <Step3
                              onClick={handleNext}
                              instanceId={objs?.instanceId}
                              instance={objs?.instance}
                            />
                            )
                          : (
                            <Step4
                              instanceId={objs?.instanceId}
                              initialEmail={objs?.email}
                            />
                            )
              )}
          {/* <Disabled /> */}
        </div>
      </div>
    </div>
  )
}
export default MffNumber
